import { useCallback, useEffect, useRef, useState } from 'react'
import {
  canAddJourneyPing,
  computeJourneyEtaMinutes,
  computeJourneyStage,
  crossedJourneyPingFractionCount,
  evaluateJourneyPingCadence,
  JOURNEY_GPS_FRESH_MS,
  JOURNEY_PING_ELAPSED_FRACTIONS,
  type JourneyStage,
} from '@shared/sessions'
import type { GeoPoint, JourneyLocationSource, SessionJourney } from '@shared/sessions/types'
import { isLiveJourneyFix, type JourneyBackfillFix } from '@shared/sessions/journey-backfill'
import {
  isPositionWatchSupported,
  watchSessionPosition,
} from '../../../services/background-location'
import {
  createJourneyBackfillQueue,
  type JourneyBackfillQueue,
  type JourneyBackfillSendMeta,
} from './journey-backfill-queue'

const STAGE_CHECK_INTERVAL_MS = 15_000

export interface JourneyPingPayload {
  lat: number
  lng: number
  timestamp: number
  accuracy?: number
  stage: JourneyStage
  etaMinutes: number
  locationSource: JourneyLocationSource
  /** Platform mock/fake-location flag — server ignores these for auto-arrival. */
  isMock?: boolean
}

/**
 * While en route, watch device GPS (Capacitor WebView / browser) and auto-send
 * journey pings:
 *
 *  - on the cadence in `shared/sessions/journey-ping-policy.ts` — every
 *    ~20 s or ~150 m, whichever comes first, and only for a fresh, accurate
 *    (≤ 100 m) fix — so the member's ETA/progress stay anchored to real
 *    positions (2026-10-06 field test: stage-transition-only pings left the
 *    member on a stale time countdown — "7 min away" vs Google Maps' 11; the
 *    60 s cadence that followed still left the smoothed bar coasting);
 *  - on stage transitions and elapsed-time boundaries (T/5 … 4T/5) as before —
 *    these are the only triggers that still fire without GPS (as `estimate`
 *    pings).
 *
 * NB: positions come from the shared Sessions position stream
 * (`services/background-location.ts`). In the native shells, while the journey
 * is en route, that stream is fed by the native BackgroundLocation plugin so
 * fixes keep arriving with the phone locked or Google Maps in front; fixes
 * buffered while the WebView was suspended are replayed in order on resume
 * (each with its own timestamp). On web it is the plain foreground watcher.
 *
 * LIVE vs BACKFILL (shared/sessions/journey-backfill.ts): a replayed fix —
 * one whose own timestamp is already too old to be live when it is delivered
 * — never touches the member-visible position/ETA and is never sent as a
 * live ping. It goes to the backfill queue (`onBackfill`), which batches,
 * downsamples and sends it to the journey track with its own timestamp.
 *
 * STALE-FIX RULE (2026-10-08 field test): a position is only ever sent as a
 * `gps` ping by the watch callback that delivered it, or — on a stage/elapsed
 * transition — while it is still fresh ({@link JOURNEY_GPS_FRESH_MS}). The
 * 15 s stage timer must NOT re-send the last known position as a fresh
 * `gps` fix: with the phone locked / Maps in front it re-stamped the same
 * coordinates minute after minute (identical lat/lng/accuracy, new
 * timestamps), so the member saw the provider "parked" for 3 minutes and then
 * teleport 2.3 km, the server's gap-aware ETA treated the stretch as
 * stationary, and the arrival-leg km got a ~140 km/h segment. Stale → the
 * transition ping goes out as `estimate` (server ignores its coordinates).
 *
 * This hook CONSUMES the position stream; it does not source it. A native
 * background-location adapter can feed the same `watchPosition`-shaped
 * callback and the cadence policy applies unchanged.
 *
 * TODO(route-matrix): optional AWS Amazon Location Service for route-aware ETA.
 * See shared/sessions/TRAVEL-ROUTING.md — stub in travel-routing.ts until infra exists.
 */
export function useEnRouteGpsTracking({
  enabled,
  journey,
  destination,
  travelDurationMs,
  checkedIn,
  onPing,
  onBackfill,
}: {
  enabled: boolean
  journey: SessionJourney | null
  destination: GeoPoint | null
  travelDurationMs: number | null
  checkedIn?: boolean
  onPing: (payload: JourneyPingPayload) => Promise<void>
  /** Receives downsampled batches of replayed (non-live) fixes — see journey-backfill.ts. */
  onBackfill?: (fixes: JourneyBackfillFix[], meta: JourneyBackfillSendMeta) => Promise<void>
}) {
  const [currentPosition, setCurrentPosition] = useState<GeoPoint | null>(null)
  const [stage, setStage] = useState<JourneyStage>(1)
  const [gpsActive, setGpsActive] = useState(false)
  const [gpsUnavailable, setGpsUnavailable] = useState(false)

  const lastPingedStageRef = useRef<JourneyStage>(1)
  const lastPingedFractionCountRef = useRef(0)
  /** Position + wall-clock time of the last ping sent — the cadence policy's anchor. */
  const lastSentRef = useRef<{ lat: number; lng: number; sentAt: number } | null>(null)
  const onPingRef = useRef(onPing)
  const journeyRef = useRef(journey)
  const currentPositionRef = useRef<GeoPoint | null>(null)
  /**
   * Reported accuracy (metres) of the latest real fix. Sent with GPS pings so
   * the server's auto-arrival gate can reject coarse fixes
   * (ARRIVAL_MAX_ACCURACY_METERS — no arrival from a km-wide network fix).
   */
  const currentAccuracyRef = useRef<number | undefined>(undefined)
  /** Device fix time of the latest real position (what a `gps` ping carries). */
  const currentFixTimestampRef = useRef<number | null>(null)
  /** Mock flag of the latest real fix — carried on live GPS pings. */
  const currentIsMockRef = useRef(false)
  /** Wall-clock moment the latest real fix ARRIVED — drives the stale gate. */
  const currentFixReceivedAtRef = useRef(0)
  const pingInFlightRef = useRef(false)
  const onBackfillRef = useRef(onBackfill)
  const backfillQueueRef = useRef<JourneyBackfillQueue | null>(null)

  onPingRef.current = onPing
  onBackfillRef.current = onBackfill
  journeyRef.current = journey
  currentPositionRef.current = currentPosition

  // One backfill queue per mount (declared before the watch effect so it
  // exists when the first fix lands); replayed fixes are handed to it from
  // the watch callback and sent as batches through `onBackfill`.
  useEffect(() => {
    const queue = createJourneyBackfillQueue({
      send: async (fixes, meta) => {
        await onBackfillRef.current?.(fixes, meta)
      },
    })
    backfillQueueRef.current = queue
    return () => {
      queue.dispose()
      if (backfillQueueRef.current === queue) backfillQueueRef.current = null
    }
  }, [])

  const recomputeStage = useCallback(
    (position: GeoPoint | null, now: number = Date.now()) => {
      const next = computeJourneyStage({
        journey: journeyRef.current,
        destination,
        currentPosition: position,
        checkedIn,
        travelDurationMs,
        now,
      })
      setStage(next)
      return next
    },
    [destination, checkedIn, travelDurationMs],
  )

  const sendPing = useCallback(
    async (opts: {
      position: GeoPoint | null
      stage: JourneyStage
      locationSource: JourneyLocationSource
    }) => {
      const activeJourney = journeyRef.current
      if (!activeJourney || !canAddJourneyPing(activeJourney) || pingInFlightRef.current) return

      const fallback = activeJourney.pings[activeJourney.pings.length - 1] ?? activeJourney.pings[0]
      const coords = opts.position ?? (fallback ? { lat: fallback.lat, lng: fallback.lng } : null)
      if (!coords) return

      // GPS-anchored ETA (the server recomputes authoritatively on write).
      const etaMinutes =
        computeJourneyEtaMinutes({
          journey: activeJourney,
          destination,
          travelDurationMs,
          currentPosition: opts.position,
        }) ?? 1

      pingInFlightRef.current = true
      try {
        await onPingRef.current({
          lat: coords.lat,
          lng: coords.lng,
          // A real fix carries ITS OWN device timestamp, never "now": the
          // server's staleness decay, gap-aware speed and arrival-leg km all
          // read this field, so a position must not look newer than it is.
          timestamp:
            opts.position && currentFixTimestampRef.current != null
              ? currentFixTimestampRef.current
              : Date.now(),
          accuracy: opts.position ? currentAccuracyRef.current : fallback?.accuracy,
          stage: opts.stage,
          etaMinutes,
          locationSource: opts.locationSource,
          ...(opts.position && currentIsMockRef.current ? { isMock: true } : {}),
        })
        lastPingedStageRef.current = Math.max(lastPingedStageRef.current, opts.stage) as JourneyStage
        if (opts.position) {
          lastSentRef.current = { lat: coords.lat, lng: coords.lng, sentAt: Date.now() }
        }
      } finally {
        pingInFlightRef.current = false
      }
    },
    [destination, travelDurationMs],
  )

  const maybePingOnTransition = useCallback(
    async (
      nextStage: JourneyStage,
      position: GeoPoint | null,
      now: number = Date.now(),
      opts: { cadenceDue: boolean } = { cadenceDue: false },
    ) => {
      const activeJourney = journeyRef.current
      if (!activeJourney || !canAddJourneyPing(activeJourney)) return

      const stageAdvanced = nextStage > lastPingedStageRef.current && nextStage >= 3
      let fractionAdvanced = false

      if (travelDurationMs != null && travelDurationMs > 0) {
        const crossed = crossedJourneyPingFractionCount(activeJourney, travelDurationMs, now)
        fractionAdvanced = crossed > lastPingedFractionCountRef.current
        if (fractionAdvanced) lastPingedFractionCountRef.current = crossed
      }

      // Cadence pings (`cadenceDue`, decided by evaluateJourneyPingCadence)
      // come only from the watch callback that just delivered a real fix. The
      // stage timer never fires them: it only knows the LAST position, which
      // is exactly the stale one when fixes have stopped (estimate pings would
      // just clone stale coordinates and add nothing for the member's ETA).
      const cadenceDue = opts.cadenceDue && position != null

      if (!stageAdvanced && !fractionAdvanced && !cadenceDue) return

      const locationSource: JourneyLocationSource = position ? 'gps' : 'estimate'
      await sendPing({ position, stage: nextStage, locationSource })
    },
    [sendPing, travelDurationMs],
  )

  useEffect(() => {
    // Same freshness gate as the stage timer, so a journey refresh while the
    // last fix is minutes old doesn't snap the stage back onto stale GPS.
    const fresh =
      currentPosition != null && Date.now() - currentFixReceivedAtRef.current <= JOURNEY_GPS_FRESH_MS
    setStage(recomputeStage(fresh ? currentPosition : null))
  }, [journey, recomputeStage, currentPosition])

  useEffect(() => {
    if (journey?.status === 'en_route') {
      lastPingedStageRef.current = Math.max(
        lastPingedStageRef.current,
        (journey.stage ?? 2) as JourneyStage,
      ) as JourneyStage
      if (travelDurationMs != null && travelDurationMs > 0) {
        lastPingedFractionCountRef.current = crossedJourneyPingFractionCount(journey, travelDurationMs)
      }
      // Seed the cadence anchor from stored pings so a remount mid-journey
      // doesn't instantly re-ping.
      const latest = journey.pings[journey.pings.length - 1]
      if (latest && (lastSentRef.current == null || latest.timestamp > lastSentRef.current.sentAt)) {
        lastSentRef.current = { lat: latest.lat, lng: latest.lng, sentAt: latest.timestamp }
      }
    }
  }, [journey?.status, journey?.pings.length, journey?.stage, travelDurationMs])

  useEffect(() => {
    if (!enabled || journey?.status !== 'en_route') {
      setGpsActive(false)
      return
    }
    if (!isPositionWatchSupported()) {
      setGpsUnavailable(true)
      return
    }

    let cancelled = false
    let gotFix = false

    const unsubscribe = watchSessionPosition(
      (stamp) => {
        if (cancelled) return
        gotFix = true
        setGpsActive(true)
        setGpsUnavailable(false)

        // Replayed (non-live) fix → backfill batch; never the live position.
        if (!isLiveJourneyFix(stamp)) {
          backfillQueueRef.current?.push(stamp)
          return
        }

        const now = Date.now()
        const point = { lat: stamp.lat, lng: stamp.lng }
        currentAccuracyRef.current = stamp.accuracy
        currentFixTimestampRef.current = stamp.timestamp
        currentIsMockRef.current = stamp.isMock === true
        currentFixReceivedAtRef.current = now
        setCurrentPosition(point)

        const nextStage = recomputeStage(point, now)
        // Time/distance cadence + freshness/accuracy gates. A fix that fails
        // the gates never goes out as `gps` — a stage transition that lands
        // on one is sent as `estimate` instead (server ignores its coords).
        const decision = evaluateJourneyPingCadence({ fix: stamp, lastSent: lastSentRef.current, now })
        const usable = decision.reason !== 'stale_fix' && decision.reason !== 'inaccurate'
        // Always await flush before the live ping. `pending` is queued.length
        // (0 once flushNow clears the buffer and awaits send), so a live fix
        // during an in-flight POST must still wait — empty flush is a no-op
        // and inFlight already serialises. Backdated arrival posts the thread
        // tile first; the live ping then sees `arrived` and does not double-push.
        void (async () => {
          await backfillQueueRef.current?.flush()
          if (cancelled) return
          await maybePingOnTransition(nextStage, usable ? point : null, now, {
            cadenceDue: decision.send,
          })
        })().catch(() => {
          if (!cancelled) setGpsUnavailable(true)
        })
      },
      () => {
        if (!cancelled) {
          setGpsUnavailable(true)
          if (!gotFix) setGpsActive(false)
        }
      },
    )

    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [enabled, journey?.status, journey?.pings.length, recomputeStage, maybePingOnTransition])

  useEffect(() => {
    if (!enabled || journey?.status !== 'en_route' || travelDurationMs == null || travelDurationMs <= 0) {
      return
    }

    const tick = () => {
      const now = Date.now()
      // The last known position only counts as a live fix while it is fresh;
      // after that the stage falls back to the time estimate and any
      // transition ping goes out as `estimate` (see STALE-FIX RULE above).
      const fresh =
        currentPositionRef.current != null &&
        now - currentFixReceivedAtRef.current <= JOURNEY_GPS_FRESH_MS
      const livePosition = fresh ? currentPositionRef.current : null
      const nextStage = recomputeStage(livePosition, now)
      void maybePingOnTransition(nextStage, livePosition, now, { cadenceDue: false }).catch(
        () => {
          setGpsUnavailable(true)
        },
      )
    }

    tick()
    const id = window.setInterval(tick, STAGE_CHECK_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [enabled, journey?.status, travelDurationMs, recomputeStage, maybePingOnTransition])

  return {
    currentPosition,
    stage,
    gpsActive,
    gpsUnavailable,
    pingFractions: JOURNEY_PING_ELAPSED_FRACTIONS,
  }
}
